import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUp, ArrowDown, Star, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { navigationId } from '@/lib/navigation';

export default function SidebarFavorites({ open, onOpenChange, items, favorites, onNavigate, returnFocusRef }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const { pinned, toggle, move, isBusy, isUpdating } = favorites;
  const name = item => {
    const label = t(item.nameKey);
    const duplicate = items.some(other => navigationId(other) !== navigationId(item) && t(other.nameKey) === label);
    return duplicate && item.favoriteGroup ? `${label} · ${t(item.favoriteGroup)}` : label;
  };
  const fold = value => value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLocaleLowerCase();
  const available = items.filter(item => !pinned.includes(navigationId(item)) && fold(name(item)).includes(fold(search.trim())));
  const actionClass = 'h-11 w-11 shrink-0';
  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent size="md" data-favorites-manager="true" onCloseAutoFocus={event => {
      event.preventDefault(); returnFocusRef.current?.focus();
    }}>
      <DialogHeader>
        <DialogTitle>{t('navigation.favorites.manage')}</DialogTitle>
        <DialogDescription>{t('navigation.favorites.description')}</DialogDescription>
      </DialogHeader>
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">{t('navigation.favorites.selected')}</span>
        <span className="text-muted-foreground tabular-nums">{pinned.length}</span>
      </div>
      {pinned.length === 0 && <p className="text-sm text-muted-foreground">{t('navigation.favorites.empty')}</p>}
      <ol className="space-y-1" aria-label={t('navigation.favorites.selected')}>
        {pinned.map((id, index) => {
          const item = items.find(candidate => navigationId(candidate) === id);
          const label = item ? name(item) : t('navigation.favorites.unavailable');
          return <li key={id} tabIndex={-1} className="flex min-w-0 items-center gap-1 rounded-xl border border-border pl-3 pr-1 py-1 focus-visible:ring-2 focus-visible:ring-ring">
            <span className="min-w-0 flex-1 text-sm"><span className="block truncate">{item ? <Link to={item.favoriteHref} onClick={onNavigate} className="rounded hover:underline focus-visible:ring-2 focus-visible:ring-ring">{label}</Link> : label}</span>
              {!item && <span className="block text-xs text-muted-foreground">{t('navigation.favorites.unavailable_hint')}</span>}
            </span>
            <Button variant="ghost" size="icon" className={actionClass} disabled={isBusy || index === 0}
              aria-label={t('navigation.favorites.up', { name: label })} onClick={event => { event.currentTarget.closest('li').focus(); move(id, -1); }}><ArrowUp className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" className={actionClass} disabled={isBusy || index === pinned.length - 1}
              aria-label={t('navigation.favorites.down', { name: label })} onClick={event => { event.currentTarget.closest('li').focus(); move(id, 1); }}><ArrowDown className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" className={actionClass} disabled={isBusy}
              aria-label={t('navigation.favorites.remove', { name: label })} onClick={() => toggle(id)}><X className="h-4 w-4" /></Button>
          </li>;
        })}
      </ol>
      <div className="border-t border-border pt-4 space-y-3">
        <Input value={search} onChange={event => setSearch(event.target.value)} placeholder={t('navigation.favorites.search')}
          aria-label={t('navigation.favorites.search')} />
        <ul className="max-h-52 overflow-y-auto space-y-1" aria-label={t('navigation.favorites.available')}>
          {available.map(item => {
            const id = navigationId(item);
            return <li key={id}><button type="button" disabled={isBusy}
              onClick={() => toggle(id)} aria-label={t('navigation.favorites.add', { name: name(item) })}
              className="w-full flex items-center gap-3 min-h-11 px-3 py-2 rounded-xl text-left text-sm hover:bg-accent disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-ring">
              <item.icon className="h-4 w-4 shrink-0 text-muted-foreground" /><span className="flex-1">{name(item)}</span><Star className="h-4 w-4 shrink-0" />
            </button></li>;
          })}
        </ul>
        {available.length === 0 && <p className="text-sm text-muted-foreground">{t('navigation.favorites.no_results')}</p>}
      </div>
      <DialogFooter><span role="status" className="mr-auto text-xs text-muted-foreground self-center">{isUpdating ? t('common.saving') : t('navigation.favorites.autosave')}</span>
        <Button onClick={() => onOpenChange(false)}>{t('common.close')}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}
